import type { Vec2, Player } from '../types.js';
import type { WorldState } from './state.js';
import type { InputSample } from '../input/input-state.js';
import { cfg } from '../config.js';
import { wrapArena } from './collision.js';
import { getUpgradeMods } from './upgrade-system.js';
import { getTraitMods } from './loadout.js';

interface MoveContext {
  lastHeading: Vec2;
  dashTimer: number; // seconds of displacement remaining
  bufferedMove: { dir: Vec2; magnitude: number } | null;
  knockback: Vec2;
  dashBufferExpiry: number; // tick
}

const EPS = 1e-8;

const contextMap = new WeakMap<Player, MoveContext>();

function getContext(player: Player): MoveContext {
  let ctx = contextMap.get(player);
  if (!ctx) {
    ctx = {
      lastHeading: { x: 0, y: 1 },
      dashTimer: 0,
      bufferedMove: null,
      knockback: { x: 0, y: 0 },
      dashBufferExpiry: -1,
    };
    contextMap.set(player, ctx);
  }
  return ctx;
}

/**
 * Reset the movement context for a player. Should be called when the player
 * entity is recycled (e.g., on run restart).
 */
export function resetPlayerMovementContext(player: Player): void {
  contextMap.delete(player);
}

/**
 * Apply an external knockback impulse to the player. Contact resolution should
 * call this rather than mutating `player.vel` directly, so movement can decay
 * the knockback independently of normal acceleration.
 */
export function applyPlayerKnockback(
  player: Player,
  dir: Vec2,
  speed: number
): void {
  const ctx = getContext(player);
  ctx.knockback.x += dir.x * speed;
  ctx.knockback.y += dir.y * speed;
}

function getPlayerHaste(player: Player): number {
  let haste = 0;
  // Tests / future systems may attach a numeric `haste` override.
  const direct = (player as unknown as { haste?: number }).haste;
  if (typeof direct === 'number') {
    haste += direct;
  }
  for (const passive of player.passives) {
    const maybe = passive as unknown as { haste?: number };
    if (typeof maybe.haste === 'number') {
      haste += maybe.haste;
    }
  }
  // Loadout SWIFT trait: run-constant dash-recharge haste (spec-loadout §1).
  haste += getTraitMods(player).dashHasteBonus;
  return haste;
}

function length(x: number, y: number): number {
  return Math.hypot(x, y);
}

function updateFocusAim(player: Player, input: InputSample): void {
  // `held` gates only the targeting overlay; the direction itself tracks the
  // pointer EVERY tick (mouse cursor, touch aim point, gamepad stick) so the
  // turret barrel and the manual-shot fallback aim where the player is
  // actually pointing, not where they last held Focus Aim. A zero input dir
  // keeps the previous direction as the fallback.
  player.focus_aim.held = input.focusAim.held;
  if (
    input.focusAim.dir.x !== 0 ||
    input.focusAim.dir.y !== 0
  ) {
    player.focus_aim.dir = input.focusAim.dir;
  }
}

function updateMovementVelocity(
  player: Player,
  input: InputSample,
  dt: number
): void {
  // Lumensurge move-speed ranks multiply the config top speed. Deterministic:
  // getUpgradeMods reads only the player's passive ranks. The loadout trait
  // move-speed share (SWIFT +, BULWARK −) joins the same additive bucket.
  const vMax =
    cfg.getNumber('player.move_speed') *
    (1 + getUpgradeMods(player).moveSpeedMult + getTraitMods(player).moveSpeedMult) *
    player.boon_move_speed_mult;
  const accel = cfg.getNumber('player.accel');
  const brake = cfg.getNumber('player.brake');
  const reverseDecel = cfg.getNumber('player.reverse_decel');

  const targetSpeed = input.moveMagnitude * vMax;

  if (targetSpeed > EPS) {
    const tx = input.moveDir.x * targetSpeed;
    const ty = input.moveDir.y * targetSpeed;
    const dx = tx - player.vel.x;
    const dy = ty - player.vel.y;
    const dist = length(dx, dy);

    // Use stronger reverse decel when the current velocity opposes the wish.
    const projection =
      player.vel.x * input.moveDir.x + player.vel.y * input.moveDir.y;
    const rate = projection < -EPS ? reverseDecel : accel;
    const maxStep = rate * dt;

    if (dist <= maxStep) {
      player.vel.x = tx;
      player.vel.y = ty;
    } else {
      const s = maxStep / dist;
      player.vel.x += dx * s;
      player.vel.y += dy * s;
    }
  } else {
    // Linear braking toward zero.
    const speed = length(player.vel.x, player.vel.y);
    if (speed <= brake * dt) {
      player.vel.x = 0;
      player.vel.y = 0;
    } else {
      const s = (brake * dt) / speed;
      player.vel.x -= player.vel.x * s;
      player.vel.y -= player.vel.y * s;
    }
  }
}

/** Ticks for one flash charge to recharge (dash cooldown ÷ haste). */
function dashRechargeTicks(player: Player): number {
  const dashCooldownSeconds = cfg.getNumber('player.dash_cooldown');
  const stepDt = 1 / cfg.getNumber('sim.step_rate_hz');
  const baseCooldownTicks = Math.round(dashCooldownSeconds / stepDt);
  const minCooldownTicks = Math.round(1.0 / stepDt);
  const haste = getPlayerHaste(player);
  return Math.max(minCooldownTicks, Math.round(baseCooldownTicks / (1 + haste)));
}

/** Flash-charge pool: 1 + Twin Flash ranks (Hades-style multi-dash). */
function maxDashCharges(player: Player): number {
  return 1 + Math.max(0, Math.floor(player.dashChargeBonus ?? 0));
}

function startDash(
  player: Player,
  ctx: MoveContext,
  dir: Vec2,
  tick: number
): void {
  const dashSpeed = cfg.getNumber('player.dash_speed');
  const dashDuration = cfg.getNumber('player.dash_duration');
  const dashIframesSeconds = cfg.getNumber('player.dash_iframes');
  const stepDt = 1 / cfg.getNumber('sim.step_rate_hz');

  player.dash.active = true;
  player.dash.dir = { x: dir.x, y: dir.y };
  player.vel.x = dir.x * dashSpeed;
  player.vel.y = dir.y * dashSpeed;

  ctx.dashTimer = dashDuration;
  ctx.bufferedMove = null;

  player.dash.iframes_until = tick + Math.round(dashIframesSeconds / stepDt);

  // Flash charges: consume one; if the pool just dropped below full and no
  // recharge is armed, start the (sequential) recharge clock.
  player.dash.charges -= 1;
  if (player.dash.charges < maxDashCharges(player) && player.dash.recharge_until <= tick) {
    player.dash.recharge_until = tick + dashRechargeTicks(player);
  }
  player.dash.cooldown_until = player.dash.recharge_until;

  // Phantom Vector: record the wake along the dash path (the sim damage
  // pass runs in enemy-ai; the trail renderer owns the look).
  const wakeDamage = player.dashWakeDamage ?? 0;
  if (wakeDamage > 0) {
    const dist = dashSpeed * dashDuration;
    player.dashWake = {
      ax: player.pos.x,
      ay: player.pos.y,
      bx: player.pos.x + dir.x * dist,
      by: player.pos.y + dir.y * dist,
      until: tick + 45, // 0.75 s at 60 Hz
      damage: wakeDamage,
      hitIds: [],
    };
  }
}

function updateDash(
  player: Player,
  ctx: MoveContext,
  dt: number
): void {
  const dashSpeed = cfg.getNumber('player.dash_speed');
  const step = Math.min(dt, ctx.dashTimer);

  player.pos.x += player.dash.dir.x * dashSpeed * step;
  player.pos.y += player.dash.dir.y * dashSpeed * step;

  ctx.dashTimer -= dt;
  // The torus has no walls: dash displacement wraps across the seam and
  // always runs its full duration.
  wrapArena(player.pos);

  if (ctx.dashTimer <= EPS) {
    player.dash.active = false;
    // Dash displacement is fixed; resume from rest so total dash displacement
    // matches the GDD acceptance value (6.08 u).
    player.vel.x = 0;
    player.vel.y = 0;
  }
}

function decayKnockback(kb: Vec2, dt: number): void {
  const decayRate = cfg.getNumber('player.knockback_decay');
  const mag = length(kb.x, kb.y);
  if (mag < EPS) return;

  const reduce = decayRate * dt;
  if (reduce >= mag) {
    kb.x = 0;
    kb.y = 0;
  } else {
    const s = (mag - reduce) / mag;
    kb.x *= s;
    kb.y *= s;
  }
}

function updateLastHeading(
  ctx: MoveContext,
  input: InputSample,
  player: Player
): void {
  if (input.moveMagnitude > EPS) {
    ctx.lastHeading = { x: input.moveDir.x, y: input.moveDir.y };
  } else {
    const speed = length(player.vel.x, player.vel.y);
    if (speed > EPS) {
      const inv = 1 / speed;
      ctx.lastHeading = { x: player.vel.x * inv, y: player.vel.y * inv };
    }
  }
}

/**
 * Update the player for one fixed simulation tick.
 *
 * This function is fully deterministic: it uses only `world`, `input`, `dt`,
 * and `tick`. No wall-clock reads are performed.
 */
export function updatePlayer(
  world: WorldState,
  input: InputSample,
  dt: number,
  tick: number
): void {
  const player = world.player;
  const ctx = getContext(player);

  updateFocusAim(player, input);
  updateLastHeading(ctx, input, player);

  // Dash buffer: a rising-edge dash press is remembered for 9 ticks (150 ms).
  if (input.dashPressed) {
    ctx.dashBufferExpiry = tick + 9;
  }

  const canDash =
    !player.dash.active &&
    player.dash.charges > 0 &&
    tick < ctx.dashBufferExpiry;

  if (canDash) {
    const dir =
      input.moveMagnitude > EPS ? input.moveDir : ctx.lastHeading;
    if (dir.x !== 0 || dir.y !== 0) {
      startDash(player, ctx, dir, tick);
      ctx.dashBufferExpiry = -1;
      // Dash SFX/haptics/dust consume this; emitted at the dash origin.
      world.events.emit(
        'dash',
        { phase: 'start', pos: { x: player.pos.x, y: player.pos.y }, dir: { x: dir.x, y: dir.y } },
        tick
      );
    }
  }

  // Flash charges recharge sequentially, one per cooldown period.
  if (
    player.dash.charges < maxDashCharges(player) &&
    tick >= player.dash.recharge_until
  ) {
    player.dash.charges += 1;
    if (player.dash.charges < maxDashCharges(player)) {
      player.dash.recharge_until = tick + dashRechargeTicks(player);
    }
    player.dash.cooldown_until = player.dash.recharge_until;
  }

  if (player.dash.active) {
    // Move input during a dash is ignored but buffered for the first
    // post-dash tick.
    if (input.moveMagnitude > EPS) {
      ctx.bufferedMove = {
        dir: { x: input.moveDir.x, y: input.moveDir.y },
        magnitude: input.moveMagnitude,
      };
    }
    updateDash(player, ctx, dt);
    if (!player.dash.active) {
      world.events.emit(
        'dash',
        { phase: 'end', pos: { x: player.pos.x, y: player.pos.y } },
        tick
      );
    }
  } else {
    // Apply any buffered move direction on the first tick after a dash ends.
    let effectiveInput = input;
    if (ctx.bufferedMove) {
      if (input.moveMagnitude <= EPS) {
        effectiveInput = {
          ...input,
          moveDir: ctx.bufferedMove.dir,
          moveMagnitude: ctx.bufferedMove.magnitude,
        };
      }
      ctx.bufferedMove = null;
    }

    updateMovementVelocity(player, effectiveInput, dt);
    player.pos.x += player.vel.x * dt;
    player.pos.y += player.vel.y * dt;
  }

  // Integrate and decay knockback independently of movement velocity.
  player.pos.x += ctx.knockback.x * dt;
  player.pos.y += ctx.knockback.y * dt;
  decayKnockback(ctx.knockback, dt);

  // Toroidal wrap: positions crossing ±100 re-enter on the opposite side.
  // Velocity is untouched — motion continues seamlessly across the seam.
  wrapArena(player.pos);

  // i-frames are tracked as absolute tick counts on `player.dash`; no extra
  // maintenance is needed here.
}
